<template>
  <!-- NB: no `relative` here — positioning comes from the parent usage
       (`absolute inset-x-0 bottom-0`), avoiding a relative/absolute conflict.
       The start-menu popup anchors to the nearest positioned ancestor. -->
  <div>
    <!-- Start menu popup -->
    <div
      v-if="menuOpen"
      class="xp-font absolute bottom-[30px] left-0 z-50 w-72 overflow-hidden rounded-t-lg shadow-[3px_3px_12px_rgba(0,0,0,0.5)]"
    >
      <div class="flex items-center gap-2 bg-gradient-to-b from-[#0058e6] to-[#00309c] p-3 text-white">
        <UserTile :size="40" />
        <div class="min-w-0">
          <p class="truncate text-sm font-bold">{{ userName }}</p>
          <p class="truncate font-mono text-[11px] text-white/75">{{ t('hero.aka') }}</p>
        </div>
      </div>
      <div class="flex bg-[var(--xp-face)]">
        <!-- Pinned programs -->
        <div class="relative flex-1 py-1">
          <button
            v-for="item in items"
            :key="item.id"
            class="xp-menubar-item flex w-full items-center gap-2 px-3 py-1.5 text-left text-[13px] text-black"
            @click="go(item.id)"
          >
            <span aria-hidden="true">{{ item.glyph }}</span> {{ item.label }}
          </button>
          <div class="mx-2 my-1 border-t border-[#d4d0c8]" aria-hidden="true" />
          <button
            class="xp-menubar-item flex w-full items-center justify-between px-3 py-1.5 text-left text-[13px] font-bold text-black"
            @click="allProgramsOpen = !allProgramsOpen"
          >
            {{ allProgramsLabel }} <span aria-hidden="true">▶</span>
          </button>
          <!-- All Programs flyout -->
          <div
            v-if="allProgramsOpen"
            class="absolute bottom-0 left-full z-50 ml-0.5 w-56 border border-[#0831d9] bg-[var(--xp-face)] py-1 shadow-[3px_3px_12px_rgba(0,0,0,0.5)]"
          >
            <button
              v-for="item in items"
              :key="item.id"
              class="xp-menubar-item flex w-full items-center gap-2 px-3 py-1.5 text-left text-[13px] text-black"
              @click="go(item.id)"
            >
              <span aria-hidden="true">{{ item.glyph }}</span> {{ item.label }}
            </button>
          </div>
        </div>
        <!-- System places -->
        <div class="xp-submenu w-32 py-1">
          <div class="px-3 py-1 text-[12px] font-bold text-[#00136e]">{{ userName }}</div>
          <div class="mx-2 my-1 border-t border-[#9db9e8]" aria-hidden="true" />
          <button
            class="xp-menu-item xp-menubar-item block w-full px-3 py-1.5 text-left text-[12px] font-bold"
            @click="go('contact')"
          >
            {{ helpLabel }}
          </button>
          <button
            v-for="loc in locales"
            :key="loc"
            class="xp-menu-item xp-menubar-item block w-full px-3 py-1.5 text-left text-[12px] font-bold"
            @click="$emit('locale', loc)"
          >
            {{ loc === 'pt-BR' ? 'Português' : 'English' }}
          </button>
        </div>
      </div>
      <div class="flex justify-end gap-2 bg-gradient-to-b from-[#0058e6] to-[#00309c] p-2">
        <button
          class="xp-font flex items-center gap-1 rounded border border-white/60 px-2 py-1 text-[12px] text-white hover:brightness-125"
          @click="logoff"
        >
          <span aria-hidden="true">⏻</span> {{ logoffLabel }}
        </button>
        <button
          class="xp-font flex items-center gap-1 rounded border border-white/60 bg-gradient-to-b from-[#e04444] to-[#8f1616] px-2 py-1 text-[12px] text-white hover:brightness-125"
          @click="power"
        >
          <span aria-hidden="true">⏻</span> {{ turnOffLabel }}
        </button>
      </div>
    </div>

    <!-- Bar -->
    <div class="xp-taskbar flex h-[30px] items-stretch">
      <button class="xp-start-btn my-0 flex items-center gap-1.5" @click="menuOpen = !menuOpen">
        <WindowsFlag :size="20" /> {{ startLabel }}
      </button>
      <div class="flex flex-1 items-center gap-1.5 overflow-x-auto px-2">
        <button
          v-for="item in items"
          :key="item.id"
          class="xp-task-btn flex items-center gap-1.5"
          :class="{ active: activeId === item.id }"
          @click="go(item.id)"
        >
          <span aria-hidden="true">{{ item.glyph }}</span> {{ item.label }}
        </button>
      </div>
      <div class="xp-tray flex items-center px-3">
        <span>{{ clock }}</span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import UserTile from './UserTile.vue'
import WindowsFlag from './WindowsFlag.vue'
import type { Locale } from '../locales'

export interface TaskItem {
  id: string
  label: string
  glyph: string
}

defineProps<{
  items: TaskItem[]
  activeId: string
  userName: string
  startLabel: string
  logoffLabel: string
  turnOffLabel: string
  allProgramsLabel: string
  helpLabel: string
  locales: Locale[]
}>()

const emit = defineEmits<{
  navigate: [id: string]
  locale: [loc: Locale]
  logoff: []
  power: []
}>()

const menuOpen = ref(false)
const allProgramsOpen = ref(false)
const { t } = useI18n()
const clock = ref('')

let timer: ReturnType<typeof setInterval> | null = null

function tick(): void {
  const d = new Date()
  clock.value = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
}

function go(id: string): void {
  menuOpen.value = false
  allProgramsOpen.value = false
  emit('navigate', id)
}

function logoff(): void {
  menuOpen.value = false
  allProgramsOpen.value = false
  emit('logoff')
}

function power(): void {
  menuOpen.value = false
  allProgramsOpen.value = false
  emit('power')
}

onMounted(() => {
  tick()
  timer = setInterval(tick, 15000)
})

onBeforeUnmount(() => {
  if (timer) clearInterval(timer)
})
</script>
